/**
 * Thin stepper composer — orchestrates the 3-step wedding creation flow.
 * Supports Multi-Template Fusion: brides can pick one or more templates
 * (built-in or custom) and the events/checklist are merged + deduplicated
 * before being applied to the wedding.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useWedding } from '../contexts/WeddingContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

import WeddingDetailsStep from './create-wedding/WeddingDetailsStep';
import TemplatePickStep from './create-wedding/TemplatePickStep';
import CustomizeTemplateStep from './create-wedding/CustomizeTemplateStep';
import StepperFooter from './create-wedding/StepperFooter';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FORM = {
  name: '',
  wedding_date: '',
  total_budget: '',
  preferred_currency: 'USD',
  partner1_name: '',
  partner1_role: '',
  partner2_name: '',
  partner2_role: '',
};

/**
 * Merge multiple template detail objects into a single fusion-template detail
 * by deduplicating events (by name) and checklist items (by title), keeping
 * the first occurrence. Templates are processed in the order the user picked
 * them so earlier selections win on conflicts.
 */
function mergeTemplateDetails(details) {
  const eventMap = new Map();
  const taskMap = new Map();
  const names = [];
  for (const d of details) {
    if (!d) continue;
    names.push(d.name);
    (d.events || []).forEach((e) => {
      if (e?.name && !eventMap.has(e.name)) eventMap.set(e.name, e);
    });
    (d.checklist || []).forEach((t) => {
      if (t?.title && !taskMap.has(t.title)) taskMap.set(t.title, t);
    });
  }
  return {
    name: names.join(' + ') || 'Fusion Wedding',
    events: Array.from(eventMap.values()),
    checklist: Array.from(taskMap.values()),
  };
}

export default function CreateWeddingModal({ open, onClose }) {
  const { createWedding, selectWedding, fetchTemplates, templates } = useWedding();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1=details, 2=template, 3=customize
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [customTemplates, setCustomTemplates] = useState([]);
  const [selectedTemplates, setSelectedTemplates] = useState([]); // array of template ids
  const [templateDetails, setTemplateDetails] = useState({}); // id -> detail
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [eventDates, setEventDates] = useState({});
  const [eventGuestCounts, setEventGuestCounts] = useState({});

  useEffect(() => {
    if (!open) return;
    fetchTemplates();
    (async () => {
      try {
        const { data } = await axios.get(`${API}/templates/custom`, { withCredentials: true });
        setCustomTemplates(data.templates);
      } catch (err) { console.error(err); }
    })();
    // Reset wizard state each time the modal opens
    setStep(1);
    setSelectedTemplates([]);
    setTemplateDetails({});
    setFormData(EMPTY_FORM);
    setSelectedEvents([]);
    setSelectedTasks([]);
    setEventDates({});
    setEventGuestCounts({});
  }, [open, fetchTemplates]);

  const fetchTemplateDetail = useCallback(async (templateId) => {
    try {
      const url = templateId.startsWith('custom_')
        ? `${API}/templates/custom/${templateId.replace('custom_', '')}`
        : `${API}/templates/${templateId}`;
      const { data } = await axios.get(url, { withCredentials: true });
      setTemplateDetails((prev) => ({ ...prev, [templateId]: data }));
    } catch (err) {
      console.error('Error fetching template detail:', err);
    }
  }, []);

  const handleSelectTemplate = (templateId) => {
    setSelectedTemplates((prev) => {
      if (prev.includes(templateId)) {
        // Drop this template + its cached detail
        setTemplateDetails((d) => {
          const next = { ...d };
          delete next[templateId];
          return next;
        });
        return prev.filter((id) => id !== templateId);
      }
      // Lazily fetch detail on first selection
      if (!templateDetails[templateId]) fetchTemplateDetail(templateId);
      return [...prev, templateId];
    });
  };

  // Merged detail across all currently selected templates (in selection order).
  const mergedDetail = useMemo(() => {
    const ordered = selectedTemplates
      .map((id) => templateDetails[id])
      .filter(Boolean);
    if (ordered.length === 0) return null;
    return mergeTemplateDetails(ordered);
  }, [selectedTemplates, templateDetails]);

  // When entering step 3 (or when merged detail changes due to template
  // selection), seed selected events/tasks to "all" so users start with the
  // full fusion plan and uncheck what they don't need.
  useEffect(() => {
    if (step !== 3 || !mergedDetail) return;
    setSelectedEvents(mergedDetail.events.map((e) => e.name));
    setSelectedTasks(mergedDetail.checklist.map((t) => t.title));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, mergedDetail?.events.length, mergedDetail?.checklist.length]);

  const toggleEvent = (name) =>
    setSelectedEvents((prev) => prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]);
  const toggleTask = (title) =>
    setSelectedTasks((prev) => prev.includes(title) ? prev.filter((t) => t !== title) : [...prev, title]);

  const handleSubmit = async (useTemplate = false) => {
    setLoading(true);
    try {
      const weddingData = {
        name: formData.name,
        wedding_date: formData.wedding_date || null,
        total_budget: parseFloat(formData.total_budget) || 0,
        preferred_currency: formData.preferred_currency,
        partner1_name: formData.partner1_name || null,
        partner1_role: formData.partner1_role || null,
        partner2_name: formData.partner2_name || null,
        partner2_role: formData.partner2_role || null,
      };
      const newWedding = await createWedding(weddingData);

      if (useTemplate && selectedTemplates.length > 0) {
        const { data } = await axios.post(`${API}/templates/apply-multi`, {
          wedding_id: newWedding.id,
          template_ids: selectedTemplates,
          selected_events: selectedEvents,
          selected_tasks: selectedTasks,
          event_dates: eventDates,
          event_guest_counts: eventGuestCounts,
        }, { withCredentials: true });
        const isFusion = selectedTemplates.length > 1;
        const fusionLabel = isFusion ? `Fusion (${selectedTemplates.length} templates)` : 'Template';
        toast.success(
          `${fusionLabel} applied: ${data.events_created} events, ${data.checklist_items_created} tasks` +
          (data.timeline_items_created ? `, ${data.timeline_items_created} timeline items` : '') +
          ' created',
        );
        if (isFusion) {
          // Discovery hint: brides who just crafted a fusion often want to
          // reuse it for siblings/friends. Settings already supports save-as-
          // template; we just nudge them so they know it exists.
          toast.info(
            'Want to reuse this fusion later? Settings → Save as Template will save your exact mix.',
            { duration: 6000 },
          );
        }
      }
      await selectWedding(newWedding);
      onClose();
    } catch (error) {
      console.error('Error creating wedding:', error);
      toast.error('Failed to create wedding');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = (e) => {
    e.preventDefault();
    if (!formData.name) return;
    setStep(2);
  };

  const budget = parseFloat(formData.total_budget) || 0;

  const titles = {
    1: 'Create New Wedding',
    2: 'Choose Templates',
    3: selectedTemplates.length > 1 ? 'Customize Fusion' : 'Customize Template',
  };
  const customizeDesc = selectedTemplates.length > 1
    ? `Combined plan from ${selectedTemplates.length} templates — events & tasks are deduplicated. Uncheck anything you don't need.`
    : `Deselect any events or tasks you don't need from the ${mergedDetail?.name || ''} template.`;
  const descriptions = {
    1: 'Enter your wedding details to get started.',
    2: 'Pick one or more cultural templates to fuse, or skip to start from scratch.',
    3: customizeDesc,
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-2xl overflow-hidden flex flex-col" style={{ maxHeight: '90vh' }}>
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="font-serif text-2xl" data-testid="create-wedding-modal-title">{titles[step]}</DialogTitle>
          <DialogDescription className="text-muted">{descriptions[step]}</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-1">
          {step === 1 && (
            <WeddingDetailsStep formData={formData} setFormData={setFormData} onNext={handleNext} />
          )}
          {step === 2 && (
            <TemplatePickStep
              templates={templates}
              customTemplates={customTemplates}
              selectedTemplates={selectedTemplates}
              onSelect={handleSelectTemplate}
            />
          )}
          {step === 3 && (
            <CustomizeTemplateStep
              templateDetail={mergedDetail}
              budget={budget}
              currency={formData.preferred_currency}
              selectedEvents={selectedEvents}
              onToggleEvent={toggleEvent}
              selectedTasks={selectedTasks}
              onToggleTask={toggleTask}
              eventDates={eventDates}
              setEventDates={setEventDates}
              eventGuestCounts={eventGuestCounts}
              setEventGuestCounts={setEventGuestCounts}
            />
          )}
        </div>

        <StepperFooter
          step={step}
          loading={loading}
          selectedTemplateCount={selectedTemplates.length}
          selectedEventCount={selectedEvents.length}
          selectedTaskCount={selectedTasks.length}
          canSubmit={Boolean(formData.name)}
          onCancel={onClose}
          onBack={() => setStep(step - 1)}
          onSkipTemplate={() => handleSubmit(false)}
          onGoToCustomize={() => {
            if (selectedTemplates.length > 0 && mergedDetail) setStep(3);
          }}
          onCreate={() => handleSubmit(true)}
        />
      </DialogContent>
    </Dialog>
  );
}
